<template>
  <div>
    <v-alert
      v-if="hours > 0 && !disabled"
      :value="true"
      :type="moreDaysNeeded ? 'warning' : 'success'"
    >
      {{
        moreDaysNeeded
          ? $tc('notEnoughDays', minimumDaysToSelect - selectedDays.length)
          : $t('enoughDays')
      }}
    </v-alert>
    <v-list
      :disabled="disabled"
      two-line>
      <v-list-tile
        v-for="(entry) in days"
        :key="entry.day">
        <v-list-tile-action>
          <v-checkbox
            v-model="entry.isActive"
            color="success"
            @change="calculateHours"
          />
        </v-list-tile-action>
        <v-list-tile-content>
          <v-list-tile-title>{{ getDayName(entry.day_id) }}</v-list-tile-title>
          <v-list-tile-sub-title v-if="entry.isActive && !moreDaysNeeded">
            {{ $t('start') }}: {{ entry.start_time }}
            <template v-if="breakTime > 0">({{ $t('inclBreaks', {breakTime}) }})</template>
          </v-list-tile-sub-title>
        </v-list-tile-content>
        <v-list-tile-action v-if="entry.isActive && !moreDaysNeeded">
          <v-btn
            icon
            @click="currentEntry = entry; dialog = true">
            <v-icon color="grey">edit</v-icon>
          </v-btn>
        </v-list-tile-action>
      </v-list-tile>
    </v-list>

    <v-dialog
      v-model="dialog"
      width="460"
    >
      <v-time-picker
        v-if="dialog"
        v-model="currentEntry.start_time"
        :landscape="true"
        format="24hr"
      />
    </v-dialog>

  </div>
</template>

<script>
import TimePicker from '@/components/global-components/inputs/TimePicker'
const maxWorkHoursPerDay = 10

export default {
  name: 'TimesheetDateTimeSelector',
  components: { TimePicker },
  props: {
    value: {
      type: Array,
      default: () => {
        return [...new Array(7)].map((el, index) => {
          return {
            day_id: index + 1,
            isActive: false,
            hours: 0,
            start_time: 0,
            end_time: 0
          }
        })
      }
    },
    dayNames: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hours: {
      type: [Number, String],
      required: true
    },
    breakTime: {
      default: null,
      type: Number
    }
  },
  data () {
    return {
      days: [],
      dialog: false,
      currentEntry: {}
    }
  },
  computed: {
    selectedDays () {
      return this.days.filter(el => el.isActive)
    },
    unselectedDays () {
      return this.days.filter(el => !el.isActive)
    },
    minimumDaysToSelect () {
      return Math.ceil(this.hours / maxWorkHoursPerDay)
    },

    moreDaysNeeded () {
      return this.selectedDays.length < this.minimumDaysToSelect
    },

    fullHoursPerDay () {
      if (this.selectedDays.length === 0) {
        return 0
      }

      return Math.floor(this.hours / this.selectedDays.length)
    },
    remainingHours () {
      if (this.selectedDays.length === 0) {
        return 0
      }
      return this.hours % this.selectedDays.length
    }
  },
  methods: {
    calculateHours () {
      if (this.moreDaysNeeded && !this.disabled) {
        return
      }
      this.unselectedDays.forEach(el => {
        const resetObj = {
          hours: 0,
          start_time: 0,
          end_time: 0
        }
        Object.assign(el, resetObj)
      })
      let remainingHoursToFill = this.remainingHours
      this.selectedDays.forEach(entry => {
        entry.hours = this.fullHoursPerDay
        if (remainingHoursToFill) {
          const possibleHoursForThisDay = maxWorkHoursPerDay - entry.hours
          const actuallyAddedHours = Math.min(
            remainingHoursToFill,
            possibleHoursForThisDay
          )
          entry.hours = entry.hours + actuallyAddedHours
          remainingHoursToFill = remainingHoursToFill - actuallyAddedHours
        }

        if (!entry.start_time) {
          entry.start_time = '08:00'
        }
        entry.end_time = this.$moment(entry.start_time, 'HH:mm')
          .add(this.breakTime, 'minutes')
          .add(entry.hours, 'hours')
          .format('HH:mm')
      })
      this.$emit('input', this.days)
    },
    getDayName (dayInWeek) {
      return this.dayNames[dayInWeek - 1]
    }
  },
  watch: {
    hours: {
      immediate: true,
      handler: 'calculateHours'
    },
    breakTime: {
      immediate: true,
      handler: 'calculateHours'
    },
    moreDaysNeeded: {
      immediate: true,
      handler () { this.$emit('moreDaysNeeded', this.moreDaysNeeded) }
    },
    dialog () {
      this.days.map(day => {
        if (day.day_id === this.currentEntry.day_id) return this.currentEntry
      })
      this.calculateHours()
    }
  },
  i18n: {
    messages: {
      en: {
        inclBreaks: '{breakTime}min break',
        notEnoughDays: 'Please select 1 more day | Please select {n} more days',
        enoughDays: 'Enough days',
        start: 'Start'
      },
      tr: {
        inclBreaks: '{breakTime}dk mola',
        notEnoughDays: 'Lütfen 1 gün daha seçiniz | Lütfen {n} adet gün daha seçiniz',
        enoughDays: 'Yeterli gün sayısı',
        start: 'Başla'
      },
      de: {
        inclBreaks: '{breakTime}min Pause',
        notEnoughDays: 'Bitte einen weiteren Tag wählen | Bitte noch mindestens {count} Tage wählen',
        enoughDays: 'Ausreichend Tage ausgewählt',
        start: 'Start'
      },
      ru: {
        enoughDays: 'Достаточно дней',
        inclBreaks: '{breakTime}мин перерыв',
        notEnoughDays: 'Пожалуйста, выберите еще 1 день | Пожалуйста, выберите больше дней: {count}',
        start: 'Начало'
      }
    }
  },
  created () {
    if (this.value) {
      this.days = this.value.map(day => {
        return Object.assign({}, day)
      })
      this.calculateHours()
    }
  }
}
</script>
